import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { FleetSection } from '../../../../../../shared/sso-workspace/fleet-page'
import { useApiListGroupUser, useApiListNodeUser } from '../../../../../../api-new/state/content'
import { useRoleOrgGroupUser, useRoleOrgNodeUser } from '../../../../../../role/hook/role-org'
import { RoleSelectUser } from '../../../../../../role/role-select/role-select-user'
import { IconContent, IconEdit, IconGroup, IconSelectListReadonly } from '../../../../../../shared/icon/icon'
import { KEPT_NOTE } from './person-words'

// The person's groups, courses and roles as the house's section cards (owner
// 2026-10-29: "Groups - None" was a large heading with a lone pencil): a
// title with a count, the items or a quiet empty line, and the edit action
// named in the card's header. While the person is removed (readOnly) there
// is no edit action, and one line above the cards (KeptNote) says why: their
// memberships are kept for a restore (content refuses changes with 409
// member.removed).

export const KeptNote = () =>
  <p className="pp-kept sw-hint" data-testid="pp-kept">
    <IconSelectListReadonly className="me-2" />{KEPT_NOTE}
  </p>

const Count = ({ items, loaded }) => (loaded ? <span className="pp-count" aria-label={`${items.length} in total`}>{items.length}</span> : null)

export const PersonTags = ({ names, Icon }) =>
  <ul className="pp-tags">
    {names.map((name, i) =>
      <li key={`${name}-${i}`} className="pp-tag">
        {Icon && <Icon className="pp-tag-icon" />}
        <span className="text-truncate">{name}</span>
      </li>)}
  </ul>

const ListSection = ({ id, title, Icon, items, loaded, failed, emptyText, editTo, editText, readOnly }) => {
  const names = items.map(item => item.name).filter(Boolean)
  return (
    <FleetSection
      id={id}
      title={<span className="pp-section-title">{title}<Count items={names} loaded={loaded} /></span>}
      actions={!readOnly && editTo
        ? <Link className="btn btn-sm sw-btn" to={editTo}><IconEdit className="me-1" />{editText}</Link>
        : null}>
      {failed && <p className="sw-hint mb-0" role="alert">{failed}</p>}
      {!failed && loaded && names.length === 0 && <p className="sw-hint mb-0" data-testid={`${id}-empty`}>{emptyText}</p>}
      {!failed && names.length > 0 && <PersonTags names={names} Icon={Icon} />}
    </FleetSection>
  )
}

// Fire and forget: a refusal stays in the list's state, never an uncaught
// rejection.
const useList = (useApiList, userID) => {
  const api = useApiList()
  useEffect(
    () => {
      Promise.resolve(api.get(`${userID}/user`)).catch(() => {})
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userID]
  )
  return api
}

const loadedOf = api => !!api.loaded

export const GroupsSection = ({ userID, readOnly }) => {
  const api = useList(useApiListGroupUser, userID)
  const role = useRoleOrgGroupUser()
  const items = (api.items || []).map(item => ({ name: item.groupName }))
  const canEdit = !!(role.accessCreate || role.accessDelete)
  return (
    <ListSection id="pp-groups" title="Groups" Icon={IconGroup} items={items} loaded={loadedOf(api)}
      failed={api.error ? "This person's groups couldn't be loaded." : ''}
      emptyText={readOnly ? 'Not in any group.' : 'No groups yet.'}
      editTo={canEdit ? `/org/user/list/edit/${userID}/group` : null} editText="Edit groups" readOnly={readOnly} />
  )
}

export const CoursesSection = ({ userID, readOnly }) => {
  const api = useList(useApiListNodeUser, userID)
  const role = useRoleOrgNodeUser()
  const items = (api.items || []).map(item => ({ name: item.nodeName }))
  const canEdit = !!(role.accessCreate || role.accessDelete)
  return (
    <ListSection id="pp-courses" title="Courses and categories" Icon={IconContent} items={items} loaded={loadedOf(api)}
      failed={api.error ? "This person's courses couldn't be loaded." : ''}
      emptyText={readOnly ? 'No courses or categories shared with them directly.' : 'No courses or categories shared with them directly yet.'}
      editTo={canEdit ? `/org/user/list/edit/${userID}/content` : null} editText="Edit courses" readOnly={readOnly} />
  )
}

// A live member's roles: the role picker (adds and removes at once). A
// removed member's: the roles content kept for them (keptRoles), read-only.
export const RolesSection = ({ userID, readOnly, keptRoles }) => {
  if (!readOnly) {
    return (
      <FleetSection id="pp-roles" title="Roles">
        <RoleSelectUser userID={userID} bare />
      </FleetSection>
    )
  }
  const names = (keptRoles || []).map(role => role.name).filter(Boolean)
  return (
    <FleetSection id="pp-roles" title={<span className="pp-section-title">Roles{Array.isArray(keptRoles) && <Count items={names} loaded />}</span>}>
      {!Array.isArray(keptRoles) && <p className="sw-hint mb-0">Their roles aren’t shown while they are removed.</p>}
      {Array.isArray(keptRoles) && names.length === 0 && <p className="sw-hint mb-0">No roles.</p>}
      {names.length > 0 && <PersonTags names={names} />}
    </FleetSection>
  )
}
